<template>
  <div>
    <add-or-update-applications-modal
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />

    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :search-filter.sync="search"
      :current-page-number.sync="currentPage"
      :show-search="true"
      :show-filter-by-default="true"
    >
      <template v-if="can('applications:CanAddNew')" #action_button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add New Application</span>
        </b-button>
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="`light-${resolveStatusVariant(data.item.status)}`"
          class="text-capitalize"
        >
          {{ getUserStatusText(data.item.status) }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item
            v-if="can('applications:CanUpdate')"
            @click="onEdit(data.item)"
          >
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="can('applications:CanUpdate')"
            @click="generateAccessToken(data.item)"
          >
            <feather-icon icon="SettingsIcon" />
            <span class="align-middle ml-50">Generate Access Token</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="can('applications:CanDelete')"
            @click="onDelete(data.item._id)"
          >
            <feather-icon icon="DeleteIcon" />
            <span class="align-middle ml-50">Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>

    <b-modal v-model="showAccessTokenModal" centered hide-footer>
      <template #modal-header>
        <div class="d-flex justify-content-between w-100">
          <h1>
            <span v-if="currentApplication">{{
              currentApplication.title
            }}</span>
            Application Access Token
          </h1>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="20"
            @click="
              () => {
                showAccessTokenModal = false;
              }
            "
          />
        </div>
      </template>
      <div v-if="accessToken" class="text-center pt-2">
        <div class="wrap-text">{{ accessToken }}</div>
        <b-button class="w-100" @click="copyAccessToken()">
          <feather-icon icon="CopyIcon" />
          Copy
        </b-button>
      </div>
      <div v-else>
        <h4>No access token generated</h4>
      </div>
    </b-modal>
  </div>
</template>

<script>
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateApplicationsModal from "@/@core/components/shared/church/AddOrUpdateApplicationsModal.vue";

import { debounce, get } from "lodash";
import { formatDate } from "@core/utils/utils";
import {
  BLink,
  BButton,
  BModal,
  BRow,
  BCol,
  BTooltip,
  BAvatar,
  BMedia,
  BBadge,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue";

const watchHandler = {
  handler() {
    this.debounceFetchAllData();
  },
  immediate: false,
};

const watchHandlerRequiresPageReset = {
  handler() {
    this.currentPage = 1;
    this.debounceFetchAllData();
  },
  immediate: false,
};

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    BDropdownItem,
    AddOrUpdateApplicationsModal,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      records: [],
      showAccessTokenModal: false,
      accessToken: "",
      currentApplication: null,

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: "title", label: "Type", sortable: false },
        { key: "description" },
        { key: "require_otp" },
        { key: "access_type" },
        { key: "status" },
        { key: "full_url" },
        {
          key: "created",
          sortable: false,
          formatter: (val) => `${formatDate(val, "dd-MM-yy h:m:s a")}`,
          label: "Date",
          class: "width-200",
        },
        { key: "actions" },
      ],
    };
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value);
      },
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value);
      },
    },
    totalRecords: {
      get() {
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value);
      },
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const { search, page = 1, limit = this.perPage } = this.$route.query;

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
        };
        await this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.applications.list(
          query
        );
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async generateAccessToken(application) {
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Confirm Access Token Generation?",
          text: `This will invalidate previously issued access tokens for ${application.title}?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, proceed.",
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-outline-danger ml-1",
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const response =
          await this.useJwt().mainService.applications.createAccessToken(
            application._id
          );
        this.accessToken = this.getValueFromSource(response, "data.data.token");
        this.currentApplication = application;

        this.showAccessTokenModal = true;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            icon: "CheckCircleIcon",
            variant: "success",
            text: "Access token genreated successfully.",
          },
        });
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error!",
              icon: "AlertTriangleIcon",
              variant: "danger",
              text: error_message,
            },
          });
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(id) {
      try {
        this.loading = true;

        const result = await this.$swal({
          title: "Confirm Delete?",
          text: "Do you want to delete this record?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, proceed.",
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-outline-danger ml-1",
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().mainService.applications.delete(id);
        await this.fetchAllData();
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            icon: "CheckCircleIcon",
            variant: "success",
            text: "Record deleted Successfully",
          },
        });
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error!",
              icon: "AlertTriangleIcon",
              variant: "danger",
              text: error_message,
            },
          });
        });
      } finally {
        this.loading = false;
      }
    },
    copyAccessToken() {
      if (!navigator || !navigator.clipboard) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: "There was an error copying access token. Please copy manually",
          },
        });
        return; 
      }
      navigator.clipboard.writeText(this.accessToken)
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Access token copied!",
              icon: "CheckCircleIcon",
              variant: "success",
              text: "Access token copied to clipboard!!.",
            },
          });
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error!",
              icon: "AlertTriangleIcon",
              variant: "danger",
              text: "There was an error copying access token. Please copy manually",
            },
          });
        });
    },
    onEdit(update) {
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData() {
      if (this.updateData) {
        this.updateData = null;
      }
    },
  },
};
</script>

<style>
.wrap-text {
  width: 100%; /* Set a width to see the effect */
  word-wrap: break-word; /* Ensures long words break and wrap */
  overflow-wrap: break-word; /* For better compatibility */
  margin-bottom: 40px;
  font-size: 15px;
}
</style>
